import { createSlice } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { fetchPickups, reprintPickupLabel } from './pickupsThunks';
import { getTodayRange } from './pickupsUtils';

const initialState = {
  data: [],
  total: 0,
  /**
   * Counted by the backend over the whole filtered set, not over the page, so
   * the cards keep meaning something while paging through results.
   */
  summary: { total: 0, drivers: 0, routes: 0, failed: 0 },
  isLoading: false,
  /**
   * Id of the pickup being reprinted, kept apart from `isLoading`: reprinting
   * one row must not blank the table underneath it.
   */
  reprintingId: null,
  error: null,
  filters: {
    ...getTodayRange(),
    state: 'all',
    seller: 'all',
    search: '',
    page: 1,
    rowsPerPage: 20,
  },
};

const pickupsSlice = createSlice({
  name: 'pickups',
  initialState,
  reducers: {
    // Anything that narrows the result set sends the reader back to page 1:
    // staying on page 4 of a listing that now has two pages shows nothing.
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload, page: 1 };
    },
    setPage: (state, action) => {
      state.filters.page = action.payload;
    },
    setRowsPerPage: (state, action) => {
      state.filters.rowsPerPage = action.payload;
      state.filters.page = 1;
    },
    setSearchFilter: (state, action) => {
      state.filters.search = action.payload;
      state.filters.page = 1;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPickups.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPickups.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.results || [];
        state.total = action.payload.count || 0;
        state.summary = action.payload.summary || initialState.summary;
      })
      .addCase(fetchPickups.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      .addCase(reprintPickupLabel.pending, (state, action) => {
        state.reprintingId = action.meta.arg;
        state.error = null;
      })
      .addCase(reprintPickupLabel.fulfilled, (state, action) => {
        state.reprintingId = null;
        // Patched in place rather than refetched: the only things that changed
        // are the pickup's own state and label, and a refetch would reshuffle
        // the page under a reader who just acted on one row.
        state.data = state.data.map((pickup) =>
          pickup.id === action.payload.id ? action.payload : pickup
        );
      })
      .addCase(reprintPickupLabel.rejected, (state, action) => {
        state.reprintingId = null;
        state.error = action.payload;
      });
  },
});

export const { setFilters, setPage, setRowsPerPage, setSearchFilter } = pickupsSlice.actions;

const persistConfig = {
  key: 'pickupsFilter',
  storage,
  whitelist: ['filters'],
};

export default persistReducer(persistConfig, pickupsSlice.reducer);
